import type { HazardRatings } from "@miter/types/common";
import { cn } from "ui2";

const NUMERIC_FIELDS = [
  { key: "health", label: "Health", bg: "tw:bg-blue-700", text: "tw:text-white" },
  { key: "flammability", label: "Flammability", bg: "tw:bg-red-600", text: "tw:text-white" },
  { key: "reactivity", label: "Reactivity", bg: "tw:bg-yellow-400", text: "tw:text-midnight" },
] as const satisfies ReadonlyArray<{ key: keyof HazardRatings; label: string; bg: string; text: string }>;

const DIAMOND_CELLS = [
  { key: "flammability", className: "tw:bg-red-600 tw:text-white" },
  { key: "reactivity", className: "tw:bg-yellow-400 tw:text-midnight" },
  { key: "health", className: "tw:bg-blue-700 tw:text-white" },
  { key: "special", className: "tw:bg-white tw:text-midnight tw:border tw:border-stroke" },
] as const satisfies ReadonlyArray<{ key: keyof HazardRatings; className: string }>;

const hasAnyRating = (ratings: HazardRatings): boolean =>
  NUMERIC_FIELDS.some((f) => ratings[f.key] != null) || ratings.special != null;

export const HazardRatingsDiamond = ({
  value,
}: {
  value: HazardRatings | null;
}): React.JSX.Element | null => {
  if (!value || !hasAnyRating(value)) return null;

  return (
    <div className="tw:@container tw:w-full tw:h-full tw:rotate-45 tw:overflow-hidden tw:shrink-0">
      <div className="tw:grid tw:grid-cols-2 tw:grid-rows-2 tw:w-full tw:h-full">
        {DIAMOND_CELLS.map(({ key, className }) => {
          const raw = value[key];
          return (
            <div
              key={key}
              className={cn("tw:flex tw:items-center tw:justify-center tw:font-bold", className)}
            >
              {raw != null && (
                <span className="tw:-rotate-45 tw:leading-none tw:text-[18cqi]">
                  {String(raw).slice(0, 5)}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const HazardRatingsBadges = ({
  className,
  value,
}: {
  className?: string;
  value?: HazardRatings | null;
}): React.JSX.Element | null => {
  if (!value || !hasAnyRating(value)) return null;

  return (
    <div className={cn("tw:flex tw:items-center tw:gap-4 tw:px-2 tw:py-1.5", className)}>
      {NUMERIC_FIELDS.map(({ key, label, bg, text }) =>
        value[key] != null ? (
          <div
            key={key}
            title={label}
            className={cn(
              "tw:flex tw:size-7 tw:shrink-0 tw:rotate-45 tw:items-center tw:justify-center tw:text-sm tw:font-bold",
              bg,
              text
            )}
          >
            <span className="tw:-rotate-45 tw:tabular-nums">{value[key]}</span>
          </div>
        ) : null
      )}
      {value.special != null ? (
        <div
          title="Special"
          className="tw:flex tw:size-7 tw:shrink-0 tw:rotate-45 tw:items-center tw:justify-center tw:border tw:border-stroke tw:bg-white tw:text-sm tw:font-bold tw:text-midnight"
        >
          <span className="tw:-rotate-45">{value.special.slice(0, 5)}</span>
        </div>
      ) : null}
    </div>
  );
};
